import { useUnit } from "effector-react"
import { Button } from "@callcenter/design-system"
import { t } from "i18next"
import { $callcenterService, $userService } from "@callcenter/effector-services"
import { IS_PWA } from "@callcenter/shared-constants"

import { userAccountModel } from "../../models"
import { AccountInformation } from "../account-information"
import { EmailConfirmation } from "../email-confirmation"
import { InterfaceSettings } from "../interface-settings"
import { ChangePasswordForm } from "../change-password-form"
import { TranscriptTranslationSettings } from "../transcript-translation-settings"
import { MobileApplicationSettings } from "../mobile-application-settings"
import { useEnableMobileNotifications } from "../../shared/hooks"

import styles from "./user-account-content.module.pcss"

type UserAccountFormProps = {
  onMobileClose?: () => void
}

export const UserAccountFormContent = ({ onMobileClose }: UserAccountFormProps) => {
  const { requestMobilePermission, setEnabledMobileNotifications, isEnabledMobileNotifications } =
    useEnableMobileNotifications()

  const actions = useUnit({
    userAccountClosed: userAccountModel.userAccountClosed,
    formSubmitted: userAccountModel.formSubmitted
  })

  const isTranscriptTranslationEnabled = useUnit($callcenterService.$permissions).has("transcript_translation")
  const isCanViewTranscription = useUnit($userService.$permissions).has("view_call_transcription")
  const isMobileApplicationEnabled = useUnit($userService.$permissions).has("mobile_application_enabled")
  const isChangePasswordAllowed = useUnit(userAccountModel.$changePasswordAllowed)
  const isEmailConfirmed = useUnit($userService.$userSettings).confirmed

  const isSubmitPending = useUnit(userAccountModel.$isSubmitPending)
  const isNeedSave = useUnit(userAccountModel.$isNeedSave)

  const isInterfaceSettingsAllowed = useUnit(userAccountModel.$isInterfaceSettingsAllowed)
  const isTranscriptTranslationSettingsAllowed = isTranscriptTranslationEnabled && isCanViewTranscription

  return (
    <>
      {isEmailConfirmed === false && <EmailConfirmation />}

      <AccountInformation />

      {isMobileApplicationEnabled && IS_PWA && (
        <MobileApplicationSettings
          isEnabledMobileNotifications={isEnabledMobileNotifications}
          setEnabledMobileNotifications={setEnabledMobileNotifications}
        />
      )}

      {isInterfaceSettingsAllowed && <InterfaceSettings />}

      {isTranscriptTranslationSettingsAllowed && <TranscriptTranslationSettings />}

      {isChangePasswordAllowed && <ChangePasswordForm />}

      {(isInterfaceSettingsAllowed || isChangePasswordAllowed || isTranscriptTranslationSettingsAllowed) && (
        <div className={styles.buttonContainer}>
          <Button
            onClick={() => {
              actions.userAccountClosed()

              if (onMobileClose) {
                onMobileClose()
              }
            }}
            kind="outline"
          >
            {t("component_commonKeys_cancelButton_cancelButton")}
          </Button>
          <Button
            isLoading={isSubmitPending}
            onClick={() => {
              if (IS_PWA) {
                requestMobilePermission()
              }

              actions.formSubmitted()

              if (!isNeedSave && onMobileClose) {
                onMobileClose()
              }
            }}
          >
            {t("component_commonKeys_saveButton_saveButton")}
          </Button>
        </div>
      )}
    </>
  )
}
